import { useState } from 'react';
import noop from 'lodash/noop';

export type CallbackFunction = (
  buttonName: string,
  customProps?: Record<string, unknown>,
) => void;
export type ContextValue = {
  onClick: CallbackFunction;
  addCallback: (buttonName: string, callback: CallbackFunction) => void;
  removeCallback: (buttonName: string, callback: CallbackFunction) => void;
};

export const MenuButtonClickedContext = React.createContext<ContextValue>({
  onClick: noop,
  addCallback: noop,
  removeCallback: noop,
});

type CallbackLookup = {
  [key: string]: Array<CallbackFunction>;
};

type MenuButtonClickedProviderProps = {
  children: React.ReactNode;
};

export const MenuButtonClickedProvider = ({
  children,
}: MenuButtonClickedProviderProps) => {
  const [callbackMap, setCallbackMap] = useState<CallbackLookup>({});
  const onClick = (
    buttonName: string,
    customProps?: Record<string, unknown>,
  ) => {
    if (!callbackMap[buttonName]) {
      return;
    }
    callbackMap[buttonName]?.forEach(cb => cb(buttonName, customProps));
  };

  const addCallback = (buttonName: string, callback: CallbackFunction) => {
    if (!callbackMap[buttonName]?.includes(callback)) {
      setCallbackMap(callbackMap => ({
        ...callbackMap,
        [buttonName]: [...(callbackMap[buttonName] ?? []), callback],
      }));
    }
  };

  /**
   * Since we are maintaining references in our callbackList to each function
   * we need to provide a way for child components to remove these references
   * when they are unmounted or it will cause a memory leak.
   */
  const removeCallback = (buttonName: string, callback: CallbackFunction) => {
    if (!callbackMap[buttonName]) {
      return;
    }
    if (callbackMap[buttonName]?.includes(callback)) {
      setCallbackMap(callbackMap => ({
        ...callbackMap,
        [buttonName]: (callbackMap[buttonName] ?? []).filter(
          c => c !== callback,
        ),
      }));
    }
  };

  return (
    <MenuButtonClickedContext.Provider
      value={{ onClick, addCallback, removeCallback }}
    >
      {children}
    </MenuButtonClickedContext.Provider>
  );
};
